import axios from 'axios';
import { clearAuthLocalState } from './authStorage';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:3041/api' : '/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're already redirecting to prevent multiple redirects
let isRedirecting = false;

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Ensure proper Bearer format
      config.headers.Authorization = `Bearer ${token}`;
      
    } else {
      // no token, skip auth header
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Reset redirect flag on successful response
    isRedirecting = false;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log error details for debugging
    if (error.response) {
      console.error(`API Error: ${error.response.status} - ${error.response.data?.error || error.response.data?.message || 'Unknown error'}`);
      console.error('Error details:', error.response.data);
    } else if (error.request) {
      console.error('API Error: No response received', error.request);
    } else {
      console.error('API Error:', error.message);
    }

    // Handle 401 errors
    if (error.response?.status === 401) {
      const errorCode = error.response.data?.error;
      
      // Don't redirect if we're already redirecting or if it's a login request
      if (!isRedirecting && !originalRequest.url?.includes('/auth/login')) {
        
        // Handle different error codes
        switch (errorCode) {
          case 'TOKEN_EXPIRED':
            console.log('Token has expired - redirecting to login');
            isRedirecting = true;
            clearAuthLocalState();
            localStorage.setItem('authMessage', 'Sesja wygasła. Zaloguj się ponownie.');
            window.location.href = '/login';
            break;

          case 'INVALID_TOKEN':
          case 'TOKEN_VERIFICATION_FAILED':
            console.log('Invalid token - clearing auth and redirecting');
            isRedirecting = true;
            clearAuthLocalState();
            localStorage.setItem('authMessage', 'Wystąpił problem z uwierzytelnieniem. Zaloguj się ponownie.');
            window.location.href = '/login';
            break;

          case 'NO_AUTH_HEADER':
          case 'INVALID_TOKEN_FORMAT':
            // Only redirect if we expect to be authenticated
            if (localStorage.getItem('token')) {
              console.log('Missing or invalid auth header with stored token - redirecting');
              isRedirecting = true;
              clearAuthLocalState();
              localStorage.setItem('authMessage', 'Zaloguj się, aby kontynuować.');
              window.location.href = '/login';
            }
            break;

          case 'USER_NOT_FOUND':
            console.log('User not found - account may have been deleted');
            isRedirecting = true;
            clearAuthLocalState();
            localStorage.setItem('authMessage', 'Nie znaleziono konta. Skontaktuj się z administratorem.');
            window.location.href = '/login';
            break;

          default:
            // Generic 401 handling
            if (localStorage.getItem('token')) {
              console.log('Generic 401 error - redirecting to login');
              isRedirecting = true;
              clearAuthLocalState();
              localStorage.setItem('authMessage', 'Zaloguj się, aby kontynuować.');
              window.location.href = '/login';
            }
        }
      }
    }
    
    // Handle 500 errors
    if (error.response?.status === 500) {
      const errorCode = error.response.data?.error;
      if (errorCode === 'JWT_SECRET_MISSING') {
        console.error('Server configuration error: JWT_SECRET is not configured');
        // This is a server config issue, not a user issue
        error.message = 'Błąd konfiguracji serwera. Skontaktuj się z administratorem.';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
